import { forwardRef } from 'react';

import { Button, ButtonProps } from '@/ux-common/components/button/button';
import { DisabledTooltip } from '@/ux-common/components/tooltip/disabled-tooltip';
import { type TooltipPositioningProps } from '@/ux-common/components/tooltip/tooltip';

interface TooltipButtonProps extends ButtonProps, Omit<TooltipPositioningProps, 'tooltipArrow'> {
  /**
   * The root span's `data-component` marker — `"TooltipButton"` unless a
   * composing component names itself. Never falls through to the inner button.
   */
  'data-component'?: string;
  disabled?: boolean;
  showTooltipWhenEnabled?: boolean;
  tooltipContent?: React.ReactNode;
  /** Class for the wrapper span — flex placement (`self-start`, `w-full`) belongs here. */
  wrapperClassName?: string;
}

/**
 * Button with a conditional tooltip, composed from the registry wrappers:
 * `DisabledTooltip` owns the wrapper span and the always-on registry arrow —
 * see that component for why a disabled button needs the span as its trigger.
 *
 * The bubble renders only when `tooltipContent` is set AND the button is
 * disabled (or `showTooltipWhenEnabled` opts in). The common case is explaining
 * why an action is unavailable.
 *
 * `delayDuration` left unset inherits the surrounding provider — the root's 0,
 * or a table island's TABLE_TOOLTIP_DELAY_MS. The tooltip-button test suite
 * pins that pass-through; don't reintroduce a default here.
 */
export const TooltipButton = forwardRef<HTMLButtonElement, TooltipButtonProps>(
  (
    {
      contentClassName,
      'data-component': dataComponent = 'TooltipButton',
      delayDuration,
      disabled,
      children,
      interactiveContent,
      showTooltipWhenEnabled = false,
      tooltipAlign,
      tooltipAlignOffset,
      tooltipContent,
      tooltipSide,
      tooltipSideOffset,
      wrapperClassName,
      ...props
    },
    ref
  ) => {
    const showTooltip = disabled || showTooltipWhenEnabled;

    return (
      <DisabledTooltip
        className={wrapperClassName}
        content={showTooltip ? tooltipContent : undefined}
        data-component={dataComponent}
        contentClassName={contentClassName}
        delayDuration={delayDuration}
        interactiveContent={interactiveContent}
        isDisabled={Boolean(disabled)}
        tooltipAlign={tooltipAlign}
        tooltipAlignOffset={tooltipAlignOffset}
        tooltipArrow
        tooltipSide={tooltipSide}
        tooltipSideOffset={tooltipSideOffset}
      >
        <Button ref={ref} disabled={disabled} {...props}>
          {children}
        </Button>
      </DisabledTooltip>
    );
  }
);

TooltipButton.displayName = 'TooltipButton';
